<template>
    <div>
        <transition name="fade" mode="out-in">
            <div
                v-if="!initialLoadComplete || (!todos.length && loadingList)"
                key="initialLoadingList"
            >
                <busy-spinner inner-class="my-5" text="Loading response items..."></busy-spinner>
            </div>
            <div
                v-else-if="!todos.length && !loadingList"
                key="initialEmptyList"
                class=""
            >
                <div class="w-100 px-3 py-3 mb-3 bg-gray-100 border rounded-lg text-center text-muted">
                    <p class="mb-2 font-size-110">There are no open items to be completed.</p>
                    <p class="mb-0 font-size-80">Please check back later, or check with your contacts for this Sourcing Event.</p>
                </div>
                <!-- <p class="text-center text-muted font-size-120">There are no open items to be completed.</p> -->
            </div>
            <div 
                v-else
                key="initialLoaded"
                :class="loadingList ? 'opacity-50' : ''"
            >  
                <p 
                    v-if="!loadingList && todos.length && !canModify"
                    class="text-danger-darker text-center font-weight-500"
                >
                    Your responses have been locked, and can no longer be modified.  
                    They are read-only.
                </p>

                <!-- LIST -->
                <div
                    v-for="todo in todos" 
                    :key="todo.id"
                    class="d-flex justify-content-start align-items-stretch p-0 w-100 mb-3 border rounded shadow-sm bg-gray-50 overflow-hidden"
                > 
                    <div 
                        class="d-flex justify-content-center align-items-center px-2 py-2 bg-primary-50 min-width-80 max-width-80"
                        style="border-left: 4px solid var(--primary);"
                    >
                        <div class="flex-grow-1 text-center text-primary-600">
                            <b-icon :icon="todo.rctd.categoryIcon" font-scale="1.7" class="mt-1"></b-icon>
                            <div class="font-size-60 mt-1 text-uppercase">{{ todo.rctd.categoryName }}</div>
                        </div>
                    </div>
                    <div class="flex-fill d-flex flex-wrap justify-content-between align-items-center py-2">
                        <div class="w-50 px-3 max-width-400 d-flex justify-content-between align-items-center">
                            <div class="w-100 d-flex flex-column justify-content-center">
                                <div class="w-100 d-flex justify-content-between align-items-baseline">
                                    <h5 class="mb-0">{{ todo.rctd.title }}</h5>
                                </div>
                                <p v-if="todo.rctd.final_instructions" class="mb-0 mt-2 text-muted small">{{ todo.rctd.final_instructions }}</p>
                            </div>
                            <div>
                                <span class="h6 mb-0 ml-3 text-uppercase font-weight-600 font-size-70" :class="todo.rctd.required ? 'text-black' : 'text-black-50'">
                                    {{ todo.rctd.required ? 'Required' : 'Optional' }}
                                </span>
                            </div>
                        </div>
                    
                        <div class="w-50 max-width-350 px-3">
                            <div 
                                v-if="todo.draftReply"
                                class="flex-fill"
                            >  <!-- DRAFT -->
                                <div 
                                    class="d-flex flex-wrap justify-content-between align-items-center"
                                >   <!-- Draft badge and buttons -->
                                    <h5 class="d-inline mb-0">
                                        <b-badge
                                            variant="soft-primary"
                                            class="text-nowrap text-uppercase"
                                        >
                                            Draft
                                        </b-badge>
                                        <span 
                                            class="mx-1"
                                            v-b-tooltip.hover 
                                            :title="todo.draftReply.reply_valid ? 'Ready to Submit' : 'Incomplete or Invalid'"
                                        >
                                            <b-icon 
                                                :icon="todo.draftReply.reply_valid ? 'check-circle-fill' : 'exclamation-circle-fill'"
                                                :variant="todo.draftReply.reply_valid ? 'success' : 'danger'" 
                                                font-scale="0.8" 
                                            ></b-icon>
                                        </span>
                                    </h5>
                                    <div>
                                        <b-button  
                                            variant="link"
                                            size="sm"
                                            :disabled="!canModify"
                                            class="text-uppercase text-decoration-none font-weight-500"
                                            v-b-tooltip.hover.ds500
                                            title="Delete this draft version"
                                            @click="deleteReply(todo.id, todo.draftReplyId)"
                                        >
                                            Delete Draft
                                            <!-- <b-icon icon="chevron-right" class="ml-1"></b-icon> -->
                                        </b-button>
                                        <b-button  
                                            variant="sharp-primary"
                                            size="sm"
                                            :disabled="!canModify"
                                            class="text-uppercase text-decoration-none font-weight-500"
                                            v-b-tooltip.hover.ds500
                                            title="Update this version"
                                            @click="getOrCreateReply(todo)"
                                        >
                                            Update
                                            <!-- <b-icon icon="chevron-right" class="ml-1"></b-icon> -->
                                        </b-button>
                                    </div>
                                </div>
                                <div v-if="todo.lastSubmittedReply" class="text-center small text-muted mt-2">
                                    <b-link class="ml-2" @click="viewReply(todo.lastSubmittedReply)">View previous version</b-link>
                                    submitted on {{formatDateTimeLocal(todo.lastSubmittedReply.submitted_on)}}
                                </div>
                            </div>
                            <div
                                v-if="!todo.draftReply && todo.lastSubmittedReply"
                                class="flex-fill"
                            >  <!-- SUBMITTED -->
                                <div 
                                    v-if="!todo.lastSubmittedReply.reply_valid" 
                                    v-b-tooltip.hover
                                    title="This item has been updated and your responses are no longer valid.  Please revise your response."
                                    class="text-danger font-size-70 font-weight-bold text-center"
                                >
                                    <b-icon icon="exclamation-circle-fill" variant="danger" class="mr-1"></b-icon>
                                    NEEDS ATTENTION
                                </div>
                                <div 
                                    class="d-flex flex-wrap justify-content-between align-items-center"
                                >   <!-- Submitted badge and buttons -->
                                    <h5 class="d-inline mb-0">
                                        <b-badge
                                            variant="soft-success"
                                            class="text-nowrap text-uppercase"
                                        >
                                            Submitted
                                        </b-badge>
                                        <span class="mx-1 small text-muted">
                                            on {{formatDateTimeLocal(todo.lastSubmittedReply.submitted_on)}}
                                        </span>
                                    </h5>
                                    <div>
                                        <b-button  
                                            variant="link"
                                            size="sm"
                                            :disabled="!canView"
                                            class="text-uppercase text-decoration-none font-weight-500"
                                            v-b-tooltip.hover.ds500
                                            title="View submitted version"
                                            @click="viewReply(todo.lastSubmittedReply)"
                                        >
                                            View
                                            <!-- <b-icon icon="chevron-right" class="ml-1"></b-icon> -->
                                        </b-button>
                                        <b-button  
                                            :variant="todo.lastSubmittedReply.reply_valid ? 'link' : 'sharp-primary'"
                                            size="sm"
                                            :disabled="!canModify"
                                            class="text-uppercase text-decoration-none font-weight-500"
                                            v-b-tooltip.hover.ds500
                                            title="Create new version"
                                            @click="getOrCreateReply(todo)"
                                        >
                                            Revise
                                            <!-- <b-icon icon="chevron-right" class="ml-1"></b-icon> -->
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="!todo.draftReply && !todo.lastSubmittedReply"
                                class="flex-fill"
                            >  <!-- NOT STARTED -->
                                <div 
                                    class="d-flex flex-wrap justify-content-between align-items-center"
                                >   <!-- Not started badge and buttons -->
                                    <h5 class="d-inline mb-0">
                                        <b-badge
                                            variant="soft-secondary"
                                            class="text-nowrap text-uppercase"
                                        >
                                            Not Started
                                        </b-badge>
                                    </h5>
                                    <div>
                                        <b-button  
                                            variant="success"
                                            size="sm"
                                            :disabled="!canModify"
                                            class="text-uppercase text-decoration-none font-weight-500"
                                            v-b-tooltip.hover.ds500
                                            title="Start work on this item"
                                            @click="getOrCreateReply(todo)"
                                        >
                                            Start
                                            <b-icon icon="play-fill" class="ml-1"></b-icon>
                                        </b-button>
                                    </div>
                                </div>
                            </div>  <!-- Add button for 'discard edits' -->
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- <h3>Nested List</h3>
        <pre>{{ todos }}</pre> -->

        <div>  <!-- Reply Modals -->

            <b-modal
                v-model="quoteSheetModalShowing"
                centered
                size="full"
                title="Quote Sheet"
                body-class="d-flex flex-column bg-gray-300 p-0"
                hide-footer
                no-close-on-esc
                @hide="handleModalHide"
                @hidden="handleModalHidden"
            >
                <quote-sheet-reply
                    :reply-id="activeQuoteSheetReplyId"
                    @close-me="quoteSheetModalShowing = false"
                ></quote-sheet-reply> 
            </b-modal>

            <b-modal
                v-model="formiumFormModalShowing"
                centered
                size="xl"
                title="Form"
                body-class="bg-gray-100 px-5 py-4"
                content-class="overflow-hidden"
                scrollable
                no-close-on-esc
                hide-footer
                hide-header
                @hide="handleModalHide"
                @hidden="handleModalHidden"
            >
                <formium-reply-html
                    :reply-id="activeFormiumReplyId"
                    :disable-submit="formiumReadOnly"
                    @cancel="formiumFormModalShowing = false"
                    @saved="formiumFormModalShowing = false"
                ></formium-reply-html> 
            </b-modal>

            <b-modal
                v-model="docRequestModalShowing"
                centered
                size="md"
                title="File Upload"
                body-class="bg-gray-100"
                content-class="overflow-hidden"
                no-close-on-esc
                hide-footer
                hide-header
                @hide="handleModalHide"
                @hidden="handleModalHidden"
            >
                <transition name="fade-fast" mode="out-in">
                    <div
                        v-if="docRequestSavedVisible"
                        key="docRequestSavedVisibleTrue"
                        class="py-5"
                    >
                        <save-success></save-success>
                    </div>
                    <doc-request-reply
                        v-else
                        key="docRequestSavedVisibleFalse"
                        :reply-id="activeDocRequestReplyId"
                        @cancel="docRequestModalShowing = false"
                        @saved="docRequestShowSaved"
                        @loaded="$emit('doc-request-reply-loaded')"
                    ></doc-request-reply>  <!-- @loaded is just a placeholder -->
                </transition>
            </b-modal>

            <b-modal
                v-model="isSmartFormModalShown"
                centered
                size="xxl"
                title="Quote Worksheet"
                body-class="bg-gray-100"
                content-class="overflow-hidden"
                no-close-on-esc
                no-close-on-backdrop
                hide-footer
                hide-header
                scrollable
                @hide="handleModalHide"
                @hidden="handleModalHidden"
            >
                <SmartFormFormio
                    :replyId="activeSmartFormReplyId"
                    @request-close="isSmartFormModalShown = false"
                />
            </b-modal>

            <!-- <SmartForm 
                :reply-id="activeSmartFormReplyId"
                :isShown="isSmartFormModalShown"
                @hide="handleModalHide"
                @hidden="handleModalHidden"
                @update:isShown="
                    (newValue) => (isSmartFormModalShown = newValue)
                "
            /> -->

        </div>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling } from '../../js/custom-vue-mixins';
import { RfxCompanyToDoReplyStatus } from '../company_todos/rfx_company_todo_classes';
import { FormatDateHelper } from '../../js/filters-helpers';
import SmartFormFormio from '../smartforms/SmartFormFormio.vue';


export default {
    name: 'PortalRfxTodoList',
    components: { 
        SmartFormFormio,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        responseId: {  // RfxResponse ID
            type: String,
            required: true,
        },
        // companyId?  do you need it?  try not...
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            initialLoadComplete: false,
            loadingList: false,
            todos: [],  // instances of class RfxCompanyToDoReplyStatus
            hasPossibleSubmit: false,  // from API, if true, we can show submit button, a submit is possible
            // REPLY MODALS
            quoteSheetModalShowing: false,
            activeQuoteSheetReplyId: '',  // put ID here 
            formiumFormModalShowing: false,
            activeFormiumReplyId: '',  // put ID here
            formiumReadOnly: false,  // set to true if you are only doing 'view' of is_submitted, and make sure to reset it
            docRequestModalShowing: false,
            activeDocRequestReplyId: '',  // put ID here
            docRequestSavedVisible: false,  // when True, shows the save-success component inside doc request reply modal -- other modals/categories have their own save-success
            isSmartFormModalShown: false,
            activeSmartFormReplyId: '',   
            // vendor permission things
            canModify: false,  // just a default - might set to false when we get API response
            canView: false,  // just a default - might set to true when we get API response
            canNewSubmit: false,  // just a default - might set to true when we get API response
        }
    },
    computed: {

    },
    mounted: function() {
        this.getList();
    },
    methods: {
        // use apiErrorHandler(error) to handle most API errors and show toasts
        getList: function() {
            this.loadingList = true;
            return suppliosApi.rfxResponse.listRfxCompanyToDosExternal(this.responseId)
                .then(response => {
                    this.hasPossibleSubmit = response.data.has_possible_submit;
                    let todoList = response.data.todos;
                    this.todos = [];  // clear the existing list so you can push new refreshed ones
                    todoList.forEach(td => {
                        this.todos.push(new RfxCompanyToDoReplyStatus(td));
                    });
                    const vendorPerms = response.data.vendor_permissions;
                    this.canModify = vendorPerms?.can_vendor_modify ?? false;  // default to False
                    this.canView = vendorPerms?.can_vendor_view ?? false;  // default to False
                    this.canNewSubmit = vendorPerms?.can_vendor_new_submit ?? false;
                    return true;
                })
                .catch(error => {
                    this.apiErrorHandler(error);
                    return false;
                })
                .then(success => {
                    this.loadingList = false;
                    this.initialLoadComplete = true;
                    return success;
                });
        },
        refreshList: function() {
            // just refresh the entire list when one is updated -- this will re-validate all Replies!  so watch for this taking too long
            // if this is too much (ie: validation takes too long) make a separate endpoint just to refresh a single RfxCompanyToDo / CompanyToDo with Reply
            this.getList();
        },
        handleModalHide: function() {
            // called when any of the modals start close / hide
            // refresh the list -- start this before 'hidden' to save some time
            this.refreshList();
        },
        handleModalHidden: function() {  // used for NEW stuff too
            // on detail modal 'hidden' -- reset things?  maybe not needed if you set everything before showing modal
            this.quoteSheetModalShowing = false;
            this.activeQuoteSheetReplyId = '';  // put ID here 
            this.formiumFormModalShowing = false;
            this.activeFormiumReplyId = '';  // put ID here
            this.formiumReadOnly = false;  // must reset this
            this.docRequestModalShowing = false;
            this.isSmartFormModalShown = false;
            this.activeDocRequestReplyId = '';  // put ID here
            this.activeFormiumReplyId = '';
            this.docRequestSavedVisible = false;
        },
        docRequestShowSaved: function() {
            // called when doc request reply modal had successful save -- toggle save success on, then close modal after timeout
            this.docRequestSavedVisible = true;
            setTimeout(() => {
                this.docRequestModalShowing = false;
            }, 2500);
        },
        getOrCreateReply: function(rfxCompanyToDoObj) {
            // call backend to get the ID of the Reply instance we should be updating
            // this moves all the logic for getting draft/submitted/cloning, etc. to the BACKEND so you don't have to deal with it here
            // even though you have some of that data here, its better to have it on backend
            let rfxCompanyToDoId = rfxCompanyToDoObj.id;
            suppliosApi.rfxCompanyToDo.getOrCreateReply(rfxCompanyToDoId, this.responseId)
                .then(response => {
                    // just log for now
                    // will send back a 423/LOCKED if the reply cannot be modified / RFX closed, etc.
                    console.log('Response from getOrCreateReply');
                    console.log(response);
                    this.showReply(response.data);
                })
                .catch(error => {
                    let status = error.response.status;
                    let errorData = error.response.data;
                    if (status == 423) {
                        console.log('Got 423 - Locked - data below');
                        console.log(errorData);
                        this.$toast.warning('You cannot update this.');
                    } else {
                        this.apiErrorHandler(error);
                    }
                })
        },
        showReply: function(replyObj) {
            // replyObj is the simple thing we got back from getOrCreateReply -- just tells us what kind of reply and the ID
            // replyObj has keys:  'category', 'reply_class' and 'reply_id'
            console.log('showReply: ', replyObj);

            const { id, reply_id, category, todo_category } = replyObj;
            const replyCategory = category || todo_category

            switch (replyCategory) {
                case "docrequest":
                    // check type also?  template or upload?
                    this.activeDocRequestReplyId = reply_id || id;
                    this.docRequestModalShowing = true;
                    break;
                case "formium_form":
                    this.activeFormiumReplyId = reply_id || id;
                    this.formiumFormModalShowing = true;
                    break;
                case "quotesheet":
                    this.activeQuoteSheetReplyId = reply_id || id;
                    this.quoteSheetModalShowing = true;
                    break;
                case "smartform":
                    this.activeSmartFormReplyId = reply_id || id;
                    this.isSmartFormModalShown = true;
            }
        },
        viewReply: function(replyObj) {
            // for READ-ONLY viewing of submitted replies
            console.log(replyObj);
            this.formiumReadOnly = true;  // this gets reset on modal close, should be OK to set it even if we're not opening a formium reply
            // get ID and category, pass off to showReply for DRY (must use the keys it expects)
            this.showReply(replyObj);
        },
        deleteReply: function(rfxCompanyToDoId, replyId) {
            // to DELETE a DRAFT reply -- must pass BOTH the rfxCompanyToDoId and the replyId
            this.loadingList = true;
            console.log(replyId);
            suppliosApi.rfxCompanyToDo.deleteDraftReply(rfxCompanyToDoId, replyId)
                .then(() => {
                    this.$toast.success('Successfully deleted draft');
                })
                .catch(error => this.apiErrorHandler(error))
                .then(() => {
                    this.refreshList();  // no need to set this.loadingList = false ?
                })
        },
        formatDateTimeLocal: function(value) {
            return FormatDateHelper(value);
        },
    }
}
</script>

<style>

</style>